import React from 'react';
import { Calendar, MapPin } from 'lucide-react';
import ScrollReveal from './ui/ScrollReveal';
import GlassCard from './ui/GlassCard';

export const Experience: React.FC = () => {
  const experiences = [
    {
      title: "Lead Generative AI Developer",
      company: "DECA Games",
      period: "October 2025 - Present",
      location: "Kolkata, West Bengal, India",
      description: "Leading generative AI initiatives and development across DECA Games. Building robust RAG pipelines, agentic systems, and production LLM applications using LangChain/LangGraph, vector databases, and observability tooling. Overseeing AI strategy and implementation for the entire organization.",
      achievements: [
        "Leading AI development initiatives across the entire DECA Games organization",
        "Building production-ready RAG pipelines and agentic AI systems",
        "Implementing LangChain/LangGraph solutions for scalable AI applications",
        "Establishing AI best practices and mentoring team on generative AI technologies"
      ]
    },
    {
      title: "Game Backend Developer",
      company: "DECA Games",
      period: "December 2019 - Present",
      location: "Kolkata, West Bengal, India",
      description: "Developing and maintaining scalable game backend systems. Working with Python, PHP, Django, NodeJS, AWS, Docker, and CI/CD tools to deliver robust gaming solutions.",
      achievements: [
        "Contributed to the scalability and reliability of online game services",
        "Implemented backend features and optimized system performance",
        "Collaborated with cross-functional teams in an agile environment"
      ]
    },
    {
      title: "Software Engineer",
      company: "HIGHBREED DEVELOPMENT INDIA PRIVATE LIMITED",
      period: "August 2018 - December 2019",
      location: "Pune, Maharashtra, India",
      description: "Worked on various software development projects, focusing on backend and full-stack development using modern technologies.",
      achievements: [
        "Delivered multiple client projects on time",
        "Enhanced application performance and reliability"
      ]
    },
    {
      title: "Senior Software Engineer",
      company: "Digital Aptech",
      period: "August 2016 - August 2018",
      location: "Not specified",
      description: "Led software engineering projects, mentoring junior developers and ensuring timely delivery of quality software solutions.",
      achievements: [
        "Managed and delivered key client projects",
        "Improved team productivity and code quality"
      ]
    },
    {
      title: "CTO",
      company: "HobbyNetworking",
      period: "May 2016 - August 2017",
      location: "Kolkata, West Bengal, India",
      description: "Oversaw technology strategy and product development for the company.",
      achievements: [
        "Directed technical operations and system architecture",
        "Led a team in developing innovative networking solutions"
      ]
    },
    {
      title: "Chief Technical Officer, Co-founder",
      company: "Packr",
      period: "June 2015 - May 2016",
      location: "Kolkata Area, India",
      description: "Led system development, technical operations, and served as tech lead for the startup.",
      achievements: [
        "Co-founded and built the technical foundation of the company",
        "Managed end-to-end technical development"
      ]
    },
    {
      title: "Chief Technical Officer, Co-founder",
      company: "Paaltao",
      period: "February 2015 - May 2016",
      location: "Virgin Islands (British)",
      description: "Idea implementer and technical co-founder for the startup.",
      achievements: [
        "Drove product vision and technical execution",
        "Established the initial technical stack and team"
      ]
    },
    {
      title: "iOS Development Team Lead",
      company: "BlueHorse Software",
      period: "August 2014 - January 2015",
      location: "Not specified",
      description: "Led the iOS app development team, delivering mobile solutions for clients.",
      achievements: [
        "Managed and mentored the iOS development team",
        "Delivered high-quality iOS applications"
      ]
    },
    {
      title: "Sencha Developer",
      company: "BlueHorse Software",
      period: "June 2014 - October 2014",
      location: "Not specified",
      description: "Developed hybrid mobile applications using Sencha Touch 2.",
      achievements: [
        "Built hybrid apps for cross-platform deployment"
      ]
    },
    {
      title: "Web Developer",
      company: "BlueHorse Software",
      period: "September 2013 - June 2014",
      location: "Not specified",
      description: "Worked on web development projects using Magento, CodeIgniter, and Yii.",
      achievements: [
        "Developed and maintained client websites and web applications"
      ]
    },
    {
      title: "Web Developer",
      company: "Soft Amass India",
      period: "November 2012 - September 2013",
      location: "Tamluk",
      description: "Built and managed software solutions for various domains including finance, healthcare, and education using PHP and MySQL.",
      achievements: [
        "Developed payroll, exam, and management systems for clients",
        "Delivered end-to-end solutions for multiple industries"
      ]
    }
  ];


  return (
    <section id="experience" className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-ink">Work Experience</h2>
          <div className="w-24 h-1 bg-brand mx-auto"></div>
          <p className="text-xs text-ink-dim mt-2">Last updated: December 2025</p>
          <p className="text-ink-dim mt-4 max-w-2xl mx-auto">
            14+ years of software development experience. View my <a href="#skills" className="text-brand hover:underline">technical skills</a> or explore <a href="#products" className="text-brand hover:underline">products I've developed</a>.
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 h-full w-[2px] bg-white/15"></div>
            
            {/* Experience items */}
            {experiences.map((exp, index) => (
              <ScrollReveal 
                key={index}
                className={`relative z-10 mb-12 md:mb-16 ${
                  index % 2 === 0 ? 'md:pr-12 md:text-right md:ml-auto' : 'md:pl-12 md:mr-auto'
                } md:w-1/2`}
              >
                <div className={`absolute top-6 md:top-8 ${
                  index % 2 === 0 ? 'left-0 md:-left-4' : 'right-0 md:-right-4'
                } w-8 h-8 rounded-full bg-gradient-to-r from-brand-gold1 to-brand-gold2 flex items-center justify-center shadow-lux-1`} aria-hidden="true">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>

                <GlassCard className="p-6 rounded-lgx border-l-4 border-brand hover:shadow-lux-2 transition-shadow">
                  <h3 className="text-xl font-bold text-ink mb-1">{exp.title}</h3>
                  <h4 className="text-lg font-semibold text-brand mb-4">{exp.company}</h4>

                  <div className="flex flex-wrap gap-4 mb-4 text-sm text-ink-dim">
                    <div className="flex items-center">
                      <Calendar size={16} className="mr-1 text-brand" />
                      <span>{exp.period}</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin size={16} className="mr-1 text-brand" />
                      <span>{exp.location}</span>
                    </div>
                  </div>

                  <p className="text-ink-dim mb-4">{exp.description}</p>

                  <div>
                    <h5 className="font-semibold text-ink mb-2">Key Achievements:</h5>
                    <ul className="list-disc list-inside text-ink-dim space-y-1">
                      {exp.achievements.map((achievement, i) => (
                        <li key={i}>{achievement}</li>
                      ))}
                    </ul>
                  </div>
                </GlassCard>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
